import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import Annnotation from "~/components/build/Show/components/Annotation";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { jobAnnotationsQueryOptions } from "~/components/build/Show/lib/queries/jobAnnotations";
import { totalAnnotationCount } from "~/components/build/Show/components/Sidebar/TreeNode/AnnotationsBadge";
import { Job } from "~/components/build/Show/lib/types";
import { JobAnnotationsEmptyState } from "~/components/build/Show/components/AnnotationsEmptyState";
import { useReactRouterNavigateHandler } from "~/components/build/Show/lib/useReactRouterNavigateHandler";
import Spinner from "~/components/shared/Spinner";

type Props = {
  job: Job;
};

export default function JobAnnotations({ job }: Props) {
  const { build } = useBuild();
  const navigateClickHandler = useReactRouterNavigateHandler();

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery(
    jobAnnotationsQueryOptions(build, job, {
      placeholderData: keepPreviousData,
    }),
  );

  const annotationsCount = totalAnnotationCount(job.statistics.annotationCountsByStyle || {});

  const handleShowMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  if (!annotationsCount) {
    return (
      <div className="my-2.5 flex w-full flex-1 items-center justify-center bg-slate-100">
        <JobAnnotationsEmptyState />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mb-3 flex-1 pt-3">
        <div className="flex w-full items-center space-x-2">
          <Spinner className="h-4 w-4" />
          <span>Fetching annotations...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3 flex w-full flex-1 flex-col pt-3" onClick={navigateClickHandler}>
      {data?.pages.flatMap((page) => {
        return page.records.map((annotation) => (
          <Annnotation key={annotation.id} annotation={annotation} />
        ));
      })}

      {hasNextPage && (
        <div className="flex w-full items-center justify-center">
          {isFetchingNextPage ? (
            <div className="space-x-2">
              <Spinner className="h-4 w-4" />
              <span>Fetching annotations...</span>
            </div>
          ) : (
            <button className="btn btn-small btn-depth" onClick={handleShowMore}>
              Show more
            </button>
          )}
        </div>
      )}
    </div>
  );
}
